import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Check, Copy, Loader2, Mail, Send, X } from 'lucide-react';
import { recordSupportOutreach, shareableLink } from '../../Services/SupportService';
import {
  SUPPORT_CHANNEL_LABELS,
  SupportChannelKey,
  SupportLink,
  SupportOutreachResult,
  SupportVisitor,
} from '../../Types/SupportType';

interface SupportOutreachModalProps {
  channel: SupportChannelKey;
  visitor: SupportVisitor | null;
  links: SupportLink[];
  onClose: () => void;
  onRecorded: (result: SupportOutreachResult) => void;
  onError: (title: string, message: string) => void;
}

const labelClass = 'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400';

const inputClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition focus:border-cyan-500/50';

const DEFAULT_COPY =
  'Hi - you visited Nexgent but did not finish creating your profile. It takes about two minutes and your progress is saved. Here is the link: {link}';

/**
 * Records a registration nudge for one visitor.
 *
 * The important thing this component does *not* do is offer a send button unconditionally. On
 * LinkedIn, WhatsApp, Telegram and Direct, an unregistered visitor has no address and Nexgent has
 * no authorised API for the platform - so the action is compose, copy, and record. Pretending
 * otherwise would either fail at send time or, worse, write "sent" into an audit trail for a
 * message no system transmitted.
 *
 * Even on email the send is an explicit opt-in, because the common case here is still a visitor
 * with no address at all.
 */
const SupportOutreachModal = ({
  channel,
  visitor,
  links,
  onClose,
  onRecorded,
  onError,
}: SupportOutreachModalProps) => {
  const [linkId, setLinkId] = useState<string>('');
  const [message, setMessage] = useState<string>(DEFAULT_COPY);
  const [attemptSend, setAttemptSend] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SupportOutreachResult | null>(null);
  const [copied, setCopied] = useState(false);

  const open = visitor !== null;

  // Only links filed under this channel, and only active ones - offering an archived link would
  // let an admin share a URL they have already retired.
  const channelLinks = useMemo(
    () => links.filter((link) => link.channel === channel && link.active),
    [links, channel]
  );

  const selectedLink = useMemo(
    () => channelLinks.find((link) => link.id === linkId) ?? null,
    [channelLinks, linkId]
  );

  // Adopt the selected link's own template the first time it is chosen, so an admin who has written
  // copy once per campaign does not retype it per visitor. Only fills an untouched default.
  React.useEffect(() => {
    if (selectedLink?.messageTemplate) {
      setMessage(selectedLink.messageTemplate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLink?.id]);

  const preview = useMemo(() => {
    if (!selectedLink) return message;
    // The link travels as one clean value - the display name when it is a full URL, otherwise
    // the short link - so the recipient sees exactly what "Ready to share" shows, and a text
    // name still routes through the short link to the tracked destination.
    const shareText = shareableLink(selectedLink);
    if (!shareText) return message;
    return message.includes('{link}')
      ? message.replace(/\{link\}/g, shareText)
      : `${message}\n\n${shareText}`;
  }, [message, selectedLink]);

  const contactable = Boolean(visitor?.email);

  const handleRecord = async () => {
    if (!visitor) return;
    setSubmitting(true);
    try {
      const response = await recordSupportOutreach({
        linkId: selectedLink?.id ?? null,
        channel,
        visitorId: visitor.visitorId,
        message,
        attemptSend,
      });
      if (response.success && response.data) {
        setResult(response.data);
        onRecorded(response.data);
      } else {
        onError('Could not record', response.message || 'Please try again.');
      }
    } catch {
      onError('Could not record', 'Failed to record the message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyPreview = async () => {
    try {
      await navigator.clipboard.writeText(preview);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      onError('Copy failed', 'Unable to copy to the clipboard.');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => !submitting && onClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
          >
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-black text-slate-100">
                  Nudge {visitor?.fullName || visitor?.email || 'visitor'}
                </h2>
                <p className="text-xs font-semibold text-slate-400">
                  {SUPPORT_CHANNEL_LABELS[channel]} · visitor {visitor?.visitorId?.slice(0, 12)}
                </p>
              </div>
              <button
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200 disabled:opacity-50"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {result ? (
              <div className="flex-1 space-y-4">
                {/*
                  A recorded send is reported as a success, not a warning. The admin did the
                  thing they came here to do; we just have no address to transmit to. Only a
                  genuine FAILED is styled as a problem.
                */}
                <div
                  className={`rounded-xl border p-4 ${
                    result.status === 'FAILED'
                      ? 'border-rose-500/30 bg-rose-500/10'
                      : 'border-emerald-500/30 bg-emerald-500/10'
                  }`}
                >
                  <p
                    className={`text-sm font-black ${
                      result.status === 'FAILED' ? 'text-rose-300' : 'text-emerald-300'
                    }`}
                  >
                    {result.status === 'SENT'
                      ? 'Sent'
                      : result.status === 'FAILED'
                        ? 'Send failed'
                        : 'Recorded'}
                  </p>
                  <p className="mt-1 text-xs font-semibold text-slate-300">{result.reason}</p>
                  {result.error && (
                    <p className="mt-1 text-[11px] font-semibold text-rose-300">{result.error}</p>
                  )}
                </div>

                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <label className={labelClass}>Message</label>
                    <button
                      onClick={copyPreview}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950/60 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400"
                    >
                      {copied ? <Check size={12} /> : <Copy size={12} />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <div className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-xs font-semibold text-slate-300">
                    {result.message}
                  </div>
                  {result.manualOnly && (
                    <p className="mt-2 flex items-start gap-2 text-[11px] font-semibold text-amber-400">
                      <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                      Copy this and send it on {SUPPORT_CHANNEL_LABELS[channel]} yourself. It is saved
                      so you can tell later what was sent.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 space-y-4 overflow-y-auto pr-1">
                {/*
                  Stated before anything is written, not discovered at send time. On four of five
                  channels this is the whole story of why the send button is a record button.
                */}
                {!contactable ? (
                  <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
                    <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-400" />
                    <p className="text-xs font-semibold text-amber-200">
                      This visitor never registered, so there is no address on file. Nexgent cannot
                      message anyone on {SUPPORT_CHANNEL_LABELS[channel]} on your behalf either -
                      there is no authorised API for it. Copy the message and send it yourself; this
                      records what you sent.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-3">
                    <Mail size={15} className="mt-0.5 shrink-0 text-cyan-400" />
                    <p className="text-xs font-semibold text-cyan-100">
                      {visitor?.email} is on file, so Nexgent can email this visitor if you choose
                      to. Left unticked, the message is recorded for you to send.
                    </p>
                  </div>
                )}

                <div>
                  <label className={labelClass}>Tracking link</label>
                  <select value={linkId} onChange={(e) => setLinkId(e.target.value)} className={inputClass}>
                    <option value="">No link (message only)</option>
                    {channelLinks.map((link) => (
                      <option key={link.id} value={link.id}>
                        {link.displayName} · {link.visitors.toLocaleString()} visitors
                      </option>
                    ))}
                  </select>
                  {channelLinks.length === 0 && (
                    <p className="mt-1 text-[10px] font-semibold text-slate-600">
                      No active {SUPPORT_CHANNEL_LABELS[channel]} links yet. Create one on the
                      Generate Link tab to attribute the follow-up.
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Message</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={7}
                    className={`${inputClass} resize-y`}
                  />
                  <p className="mt-1 text-[10px] font-semibold text-slate-600">
                    {'{link}'} is replaced with the display name as a hyperlink, e.g. [test](url).
                  </p>
                </div>

                {contactable && (
                  <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={attemptSend}
                      onChange={(e) => setAttemptSend(e.target.checked)}
                      className="h-4 w-4 accent-cyan-500"
                    />
                    <Mail size={15} className="text-cyan-400" />
                    <span className="text-xs font-bold text-slate-200">
                      Have Nexgent email {visitor?.email}
                    </span>
                  </label>
                )}
              </div>
            )}

            <div className="mt-6 flex gap-3">
              {result ? (
                <button
                  onClick={onClose}
                  className="flex-1 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 py-3 text-sm font-black text-slate-950 transition hover:from-cyan-400 hover:to-teal-400"
                >
                  Done
                </button>
              ) : (
                <>
                  <button
                    onClick={onClose}
                    disabled={submitting}
                    className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRecord}
                    disabled={submitting || !message.trim()}
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 py-3 text-sm font-black text-slate-950 transition hover:from-cyan-400 hover:to-teal-400 disabled:opacity-60"
                  >
                    {submitting ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                    {attemptSend && contactable ? 'Send & Record' : 'Record Nudge'}
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SupportOutreachModal;
